import React, { useCallback } from "react";
import { Linking, Text, View } from "react-native";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { useServices } from "../../contexts/ServiceContext";
import { Screen, useApplication } from "../../contexts/ApplicationContext";
import { BackButton } from "../buttons/BackButton";
import { SettingsButton } from "../buttons/SettingsButton";
import { AccountIcon } from "../icons/AccountIcon";
import { BackIcon } from "../icons/BackIcon";
import { SettingsIcon } from "../icons/SettingsIcon";
import { GlobalStyles } from "../style/Style";
import { Menu, MenuConfig } from "./Menu";
import { useEvent } from "../../contexts/EventContext";

interface TopNavigationProps {
    signOut: () => void;
    signIn: () => void;
}

export const TopNavigation: React.FC<TopNavigationProps> = ({ signOut, signIn }) => {
    const { fpUser } = useServices();
    const { appState, appDispatch } = useApplication();
    const { reviewPicksState, setReviewState, clearEventContext } = useEvent();
    const { pageTitle } = appState;
    const styles = useDynamicStyles(calculateStyles);

    const privacyOnPress = () => {
        Linking.openURL("https://freeplay.twinspires.com/privacy-policy");
        appDispatch({ type: "setMenuVisible", menuVisible: false });
    };

    const termsOnPress = () => {
        Linking.openURL("https://freeplay.twinspires.com/terms-conditions");
        appDispatch({ type: "setMenuVisible", menuVisible: false });
    };

    const supportOnPress = () => {
        Linking.openURL("mailto:Product.Support@twinspires.com");
        appDispatch({ type: "setMenuVisible", menuVisible: false });
    };

    const signInOnPress = () => {
        signIn();
        appDispatch({ type: "setMenuVisible", menuVisible: false });
    };

    const signOutOnPress = () => {
        signOut();
        clearEventContext();
        appDispatch({ type: "setMenuVisible", menuVisible: false });
    };

    const centeredPageTitle: boolean =
        appState.currentScreen === Screen.EVENT || appState.currentScreen === Screen.NULL;

    const goBack = useCallback(() => {
        switch (appState.currentScreen) {
            case Screen.EVENT: {
                if (reviewPicksState) {
                    setReviewState(false);
                } else {
                    appDispatch({ type: "showHome" });
                }
                break;
            }
            default:
                appDispatch({ type: "showHome" });
                break;
        }
    }, [appDispatch, appState.currentScreen, reviewPicksState, setReviewState]);

    const hasBackButton = !(
        appState.currentScreen === Screen.HOME ||
        appState.currentScreen === Screen.LEADERBOARD ||
        appState.currentScreen === Screen.MY_PICKS ||
        appState.freeplayFocusedExperience === true
    );

    const menuConfig: MenuConfig = {
        privacyOnPress,
        termsOnPress,
        supportOnPress,
        signInOnPress,
        signOutOnPress,
    };

    const IconColour = appState.menuVisible
        ? EStyleSheet.value(GlobalStyles.ACTIVE_ICON_FILL)
        : EStyleSheet.value(GlobalStyles.DEFAULT_ICON_FILL);

    return (
        <View style={styles.topNavigationContainer}>
            {hasBackButton && (
                <BackButton
                    icon={<BackIcon fill={EStyleSheet.value(GlobalStyles.DEFAULT_ICON_FILL)} />}
                    onPress={goBack}
                />
            )}
            {!hasBackButton && centeredPageTitle && <View style={styles.backButtonContainer}></View>}

            <Text style={[styles.pageTitle, centeredPageTitle ? styles.centeredPageTitle : {}]}>{pageTitle}</Text>
            <SettingsButton
                isActive={appState.menuVisible}
                icon={fpUser ? <AccountIcon fill={IconColour} /> : <SettingsIcon fill={IconColour} />}
                onPress={() => appDispatch({ type: "setMenuVisible", menuVisible: !appState.menuVisible })}
                hasMargin={!centeredPageTitle}
            />
            {appState.menuVisible && <Menu menuConfig={menuConfig} loggedIn={!!fpUser} />}
        </View>
    );
};

// - Set Line Height to Font Size
// - Set Top Margin to var (~10-15-idk)
// - Divs on left/right of title need flex-shrink 0
// - Wiggle from there :+1:

const calculateStyles = () => {
    return EStyleSheet.create({
        backButtonContainer: {
            height: "20px",
            width: 40,
        },
        topNavigationContainer: {
            zIndex: 2,
            minHeight: 50,
            paddingHorizontal: 10,
            backgroundColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
            flexDirection: "row",
            alignItems: "flex-start",
        },
        pageTitle: {
            lineHeight: 24,
            fontSize: 24,
            marginVertical: 13,
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
            color: () => EStyleSheet.value(GlobalStyles.NAVIGATION_TEXT),
        },
        centeredPageTitle: {
            textAlign: "center",
            marginHorizontal: "auto",
        },
    });
};
